'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Users, Star, Clock, CheckCircle2, Phone, MessageCircle, ChevronRight, Award, BookOpen, TrendingUp, GraduationCap } from 'lucide-react';
import Link from 'next/link';

interface Counsellor {
  id: string;
  name: string;
  specialization: string;
  experience: string;
  rating: number;
  sessions: number;
  availability: string;
  languages: string[];
  avatar: string;
}

const counsellors: Counsellor[] = [
  {
    id: 'c1', name: 'Dr. Priya Sharma', specialization: 'Engineering & Technology',
    experience: '12 Years', rating: 4.9, sessions: 3240, availability: 'Available Today',
    languages: ['Hindi', 'English'], avatar: '👩‍🏫',
  },
  {
    id: 'c2', name: 'Prof. Rajesh Kumar', specialization: 'Medical & Healthcare',
    experience: '15 Years', rating: 4.8, sessions: 2890, availability: 'Available Tomorrow',
    languages: ['Hindi', 'English', 'Tamil'], avatar: '👨‍⚕️',
  },
  {
    id: 'c3', name: 'Ms. Ananya Verma', specialization: 'Management & MBA',
    experience: '8 Years', rating: 4.9, sessions: 1980, availability: 'Available Today',
    languages: ['English', 'Bengali'], avatar: '👩‍💼',
  },
  {
    id: 'c4', name: 'Adv. Suresh Nair', specialization: 'Law & Legal Studies',
    experience: '10 Years', rating: 4.7, sessions: 1450, availability: 'Available This Week',
    languages: ['English', 'Malayalam', 'Hindi'], avatar: '⚖️',
  },
];

const benefits = [
  { icon: <Award size={20} />, title: 'Certified Experts', desc: 'All counsellors are certified with 8+ years of experience' },
  { icon: <Clock size={20} />, title: 'Flexible Timing', desc: 'Book sessions at your convenience — morning to night' },
  { icon: <MessageCircle size={20} />, title: 'Multi-language Support', desc: 'Counselling available in 12+ regional languages' },
  { icon: <CheckCircle2 size={20} />, title: '100% Free', desc: 'No hidden charges — completely free for all students' },
];

const topics = [
  { icon: <GraduationCap size={18} />, label: 'College Selection' },
  { icon: <BookOpen size={18} />, label: 'Course Guidance' },
  { icon: <TrendingUp size={18} />, label: 'Career Planning' },
  { icon: <Award size={18} />, label: 'Scholarship Help' },
  { icon: <Users size={18} />, label: 'Admission Process' },
  { icon: <Star size={18} />, label: 'Study Abroad' },
];

export default function CounsellingPage() {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', stream: '', topic: '', preferredDate: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <div className="bg-secondary text-white py-12 px-4 lg:px-8 xl:px-10 2xl:px-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-primary blur-3xl" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-3">
              <Link href="/home" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Student Counselling</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-primary/20 rounded-full px-4 py-1.5 text-sm text-primary mb-5">
                  <Users size={14} />
                  <span>Free Expert Counselling</span>
                </div>
                <h1 className="text-4xl font-extrabold text-white mb-4 leading-tight">
                  Get Personalised<br />College Guidance
                </h1>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Our certified counsellors have guided 5 lakh+ students to their dream colleges. Book a free 30-minute session today.
                </p>
                <div className="flex flex-wrap gap-3">
                  {topics.map((t) => (
                    <span key={t.label} className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-sm text-slate-200 border border-white/10">
                      {t.icon} {t.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((b) => (
                  <div key={b.title} className="bg-white/10 rounded-2xl p-4 border border-white/10">
                    <div className="text-primary mb-2">{b.icon}</div>
                    <p className="font-bold text-white text-sm mb-1">{b.title}</p>
                    <p className="text-slate-300 text-xs leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Counsellors */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Meet Our Counsellors</h2>
              <div className="space-y-4">
                {counsellors.map((c) => (
                  <div key={c.id} className="bg-card rounded-2xl border border-border p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center text-3xl shrink-0">
                        {c.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <h3 className="font-bold text-foreground">{c.name}</h3>
                            <p className="text-sm text-muted-foreground">{c.specialization}</p>
                          </div>
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${c.availability.includes('Today') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.availability}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 mb-3">
                          <div className="flex items-center gap-1">
                            <Star size={12} className="text-amber-400 fill-amber-400" />
                            <span className="font-bold text-foreground">{c.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users size={12} />
                            <span>{c.sessions.toLocaleString()} sessions</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            <span>{c.experience}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {c.languages.map((lang) => (
                            <span key={lang} className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full">{lang}</span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
                            <Phone size={12} /> Book Session
                          </button>
                          <button className="btn-secondary text-xs py-1.5 px-4 flex items-center gap-1.5">
                            <MessageCircle size={12} /> Chat Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking form */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Book a Free Session</h2>
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
                  <CheckCircle2 size={56} className="text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">Session Booked!</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Our counsellor will contact you within 24 hours on your provided phone number.
                  </p>
                  <button onClick={() => setSubmitted(false)} className="btn-primary">Book Another Session</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name *</label>
                      <input
                        type="text" required placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input-base text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number *</label>
                      <input
                        type="tel" required placeholder="+91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-base text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                    <input
                      type="email" placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-base text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Stream of Interest *</label>
                      <select
                        required value={formData.stream}
                        onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
                        className="select-base text-sm"
                      >
                        <option value="">Select stream</option>
                        {['Engineering', 'Medical', 'Management', 'Law', 'Arts', 'Commerce', 'Science'].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Counselling Topic *</label>
                      <select
                        required value={formData.topic}
                        onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                        className="select-base text-sm"
                      >
                        <option value="">Select topic</option>
                        {topics.map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Preferred Date</label>
                    <input
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                      className="input-base text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Your Query / Message</label>
                    <textarea
                      rows={3} placeholder="Describe your query or what you need help with..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="input-base text-sm resize-none"
                    />
                  </div>
                  <button type="submit" className="btn-primary w-full py-3 text-base font-bold">
                    Book Free Counselling Session
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    By submitting, you agree to be contacted by our counselling team. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
